const {
	MEDIA_TYPE_3D_MODEL,
	firstNon3dRawImage,
	is3dModelRawImage,
	pinModel3dToSlot2,
	resolveModelUrlFromRawImage,
} = require('./model-3d.js');

const MEDIA_TYPE_YOUTUBE = 'YOUTUBE';

/** Matches Common ProductConstants::IMAGE_TYPE_LOGO and legacy Dust make_part_source checks. */
const LOGO_IMAGE_TYPES = new Set(['LOGO', 'make_part_source']);

/**
 * @param {Record<string, unknown>|null|undefined} image
 * @param {'main'|'preview'|'thumb'} sizeKey
 * @returns {string}
 */
function readNestedImageUrl(image, sizeKey) {
	return image?.[sizeKey]?.url ?? '';
}

/**
 * Maps one AJAX image record from ProductsALI::buildProductImages to island gallery item props.
 *
 * @param {Record<string, unknown>} rawImage
 * @param {string} activeMainUrl
 * @returns {{
 *   id: string,
 *   type: string,
 *   icon: string|null,
 *   cssClass: string,
 *   main: string,
 *   preview: string,
 *   thumb: string,
 *   modelUrl: string,
 *   active: boolean,
 *   caption: string,
 * }}
 */
function buildGalleryItemFromRawImage(rawImage, activeMainUrl) {
	const imageId = rawImage.image_id ?? '';
	const is3dModel = is3dModelRawImage(rawImage);
	const type = is3dModel ? MEDIA_TYPE_3D_MODEL : (rawImage.type ?? 'OTHER');

	let mainUrl = readNestedImageUrl(rawImage, 'main');
	let previewUrl = readNestedImageUrl(rawImage, 'preview');
	let thumbUrl = readNestedImageUrl(rawImage, 'thumb');
	let icon = null;
	let modelUrl = '';

	if (is3dModel) {
		modelUrl = resolveModelUrlFromRawImage(rawImage);
		mainUrl = '';
		previewUrl = '';
		thumbUrl = '';
		icon = 'cube3dRotation';
	} else if (type === MEDIA_TYPE_YOUTUBE) {
		mainUrl = `https://img.youtube.com/vi/${imageId}/maxresdefault.jpg`;
		previewUrl = `https://img.youtube.com/vi/${imageId}/sddefault.jpg`;
		thumbUrl = `https://img.youtube.com/vi/${imageId}/mqdefault.jpg`;
		icon = 'fa-youtube-play';
	}

	return {
		id: imageId,
		type,
		icon,
		cssClass: `type-${type.toLowerCase()}`,
		main: mainUrl || previewUrl || thumbUrl,
		preview: previewUrl || mainUrl || thumbUrl,
		thumb: thumbUrl || previewUrl || mainUrl,
		modelUrl,
		active: activeMainUrl !== '' && mainUrl === activeMainUrl,
		caption: rawImage.caption ?? '',
	};
}

/**
 * Maps AJAX variant image payloads to product_media island gallery props.
 *
 * @param {unknown} rawImages
 * @param {{
 *   fallbackImages?: unknown[],
 *   catalog?: string,
 *   skuStripped?: string,
 * }} [options]
 * @returns {{
 *   catalog: string,
 *   skuStripped: string,
 *   productImages: Array<{
 *     id: string,
 *     type: string,
 *     icon: string|null,
 *     cssClass: string,
 *     main: string,
 *     preview: string,
 *     thumb: string,
 *     modelUrl: string,
 *     active: boolean,
 *     caption: string,
 *   }>,
 *   mainImage: { mainUrl: string, previewUrl: string, caption: string }|null,
 *   makeLogo: { mainUrl: string }|null,
 *   hasYoutube: boolean,
 *   initialSelectedIndex: number,
 * }}
 */
function buildGalleryPropsFromAjaxImages(rawImages, options = {}) {
	const fallbackImages = Array.isArray(options.fallbackImages) ? options.fallbackImages : [];
	const sourceImages = Array.isArray(rawImages) && rawImages.length > 0 ? rawImages : fallbackImages;

	let galleryImages = sourceImages.filter((image) => image !== null && typeof image === 'object');
	let heroImage = firstNon3dRawImage(galleryImages);
	let makeLogo = null;

	if (galleryImages.length === 1 && heroImage !== null && LOGO_IMAGE_TYPES.has(heroImage.type ?? '')) {
		const logoMainUrl = readNestedImageUrl(heroImage, 'main');
		makeLogo = logoMainUrl === '' ? null : { mainUrl: logoMainUrl };
		galleryImages = [];
		heroImage = null;
	}

	const activeMainUrl = heroImage !== null
		? readNestedImageUrl(heroImage, 'main')
		: '';
	const productImages = pinModel3dToSlot2(
		galleryImages.map((rawImage) => buildGalleryItemFromRawImage(rawImage, activeMainUrl)),
	);

	let mainImage = null;
	if (heroImage !== null) {
		const mainUrl = readNestedImageUrl(heroImage, 'main');
		const previewUrl = readNestedImageUrl(heroImage, 'preview');
		if (mainUrl !== '' || previewUrl !== '') {
			mainImage = { mainUrl, previewUrl, caption: heroImage.caption ?? '' };
		}
	}

	const activeIndex = productImages.findIndex((image) => image.active);

	return {
		catalog: options.catalog ?? '',
		skuStripped: options.skuStripped ?? '',
		productImages,
		mainImage,
		makeLogo,
		hasYoutube: productImages.some((image) => image.type === MEDIA_TYPE_YOUTUBE),
		initialSelectedIndex: activeIndex === -1 ? 0 : activeIndex,
	};
}

module.exports = {
	buildGalleryPropsFromAjaxImages,
};
